import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from './firebase';
import { getUserData, createUserDocument } from './utils/userService';
import './LandingPage.css'; // LandingPage için özel CSS
import { useLandingPageCleanup } from './contexts/LandingPageContext';
import { User } from 'lucide-react';

const LandingPage = () => {
  const { finishCleanup, startCleanup } = useLandingPageCleanup();
  const location = useLocation();
  const navigate = useNavigate();
  const [cssLoaded, setCssLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const videoRef = useRef(null);
  const pricingCardRef = useRef(null);
  const [pricingCardViewed, setPricingCardViewed] = useState(false);
  const info2SectionRef = useRef(null);
  const info4SectionRef = useRef(null);
  const [info2SectionViewed, setInfo2SectionViewed] = useState(false);
  const [info4SectionViewed, setInfo4SectionViewed] = useState(false);

  // Ücretsiz görüşme kayıt formu state'leri
  const [consultationForm, setConsultationForm] = useState({
    firstName: '',
    email: '',
    phoneNumber: '',
    password: ''
  });
  const [consultationFormError, setConsultationFormError] = useState('');
  const [consultationFormSuccess, setConsultationFormSuccess] = useState(false);
  const [consultationFormLoading, setConsultationFormLoading] = useState(false);
  
  // Video overlay başlık state'i
  const [videoOverlayText, setVideoOverlayText] = useState('');

  // URL parametresinden kupon kodunu al ve localStorage'a kaydet
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const couponParam = searchParams.get('code'); // ?code=yasasin50
    
    if (couponParam) {
      const normalizedCoupon = couponParam.trim().toLowerCase();
      // Geçerli kupon kodlarını kontrol et (şu an sadece yasasin50)
      if (normalizedCoupon === 'yasasin50') {
        localStorage.setItem('referral_coupon_code', normalizedCoupon);
        console.log('✅ Referral kupon kodu kaydedildi:', normalizedCoupon);
        
        // URL'den parametreyi temizle (temiz URL için)
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('code');
        const newUrl = newSearchParams.toString() 
          ? `${location.pathname}?${newSearchParams.toString()}` 
          : location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, location.pathname]);

  // Auth state kontrolü ve premium kontrolü
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const data = await getUserData(currentUser.uid);
          setUserData(data);
        } catch (error) {
          console.error('Kullanıcı verisi alınırken hata:', error);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Pricing card görünürlük takibi
  useEffect(() => {
    // CSS yüklenene kadar bekle
    if (!cssLoaded) return;
    
    if (!pricingCardRef.current || pricingCardViewed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !pricingCardViewed) {
            // Google Analytics event tracking (GA4)
            if (window.gtag) {
              window.gtag('event', 'buy_card_view', {
                event_category: 'engagement',
                event_label: 'Pricing Card Görüntülendi',
                card_location: 'Pricing Section'
              });
            }
            setPricingCardViewed(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.3 // Element'in %30'u görünür olduğunda tetikle
      }
    );

    observer.observe(pricingCardRef.current);

    return () => {
      observer.disconnect();
    };
  }, [cssLoaded, pricingCardViewed]);

  // Section görünürlük takibi - Hakkımız Section (Info 2)
  useEffect(() => {
    // CSS yüklenene kadar bekle
    if (!cssLoaded) return;
    
    // Ref'in bağlanmasını bekle
    if (!info2SectionRef.current || info2SectionViewed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !info2SectionViewed) {
            if (window.gtag) {
              window.gtag('event', 'lansman_video_section_view', {
                event_category: 'page_view',
                event_label: 'Lansman Video Section Görüntülendi'
              });
            }
            setInfo2SectionViewed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(info2SectionRef.current);
    return () => observer.disconnect();
  }, [cssLoaded, info2SectionViewed]);

  // Section görünürlük takibi - Lansman Video Section (Info 4)
  useEffect(() => {
    // CSS yüklenene kadar bekle
    if (!cssLoaded) return;
    
    if (!info4SectionRef.current || info4SectionViewed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !info4SectionViewed) {
            if (window.gtag) {
              window.gtag('event', 'egitmenlerimiz_section_view', {
                event_category: 'page_view',
                event_label: 'Eğitmenlerimiz Section Görüntülendi'
              });
            }
            setInfo4SectionViewed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(info4SectionRef.current);
    return () => observer.disconnect();
  }, [cssLoaded, info4SectionViewed]);

  // Document title and SEO
  useEffect(() => {
    document.title = 'Yaşasın Hamileyim - Online Hamilelik Eğitimi';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Yaşasın Hamileyim - Türkiye\'nin en kapsamlı hamilelik eğitimi. Op.Dr. Emre Uysal\'ın 20 yıllık tecrübesi ile hazırlanan 135 videoluk eğitim programı. Doğum hazırlığı, emzirme, bebek bakımı ve hamilelik süreciniz hakkında her şey.');
    }
    
    // Keywords meta tag'ini güncelle
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.setAttribute('content', 'yasasinhamileyim, yaşasın hamileyim, yasasınhamileyim, yasasin hamileyim, yaşasınhamileyim, hamilelik eğitimi, gebelik eğitimi, doğum hazırlığı, emzirme eğitimi, bebek bakımı, hamilelik rehberi, Op.Dr. Emre Uysal');
  }, []);

  // CSS'leri dinamik olarak yükle ve yüklenmesini bekle
  useEffect(() => {
    // Cleanup başladığını işaretle
    finishCleanup(); // Önce cleanup'ı tamamla (eğer önceki sayfadan kaldıysa)
    
    // Body ve HTML'e class ekle - LandingPage aktifken
    document.body.classList.add('landing-page-active');
    document.documentElement.classList.add('landing-page-active');

    // CSS dosyalarını yükle
    const cssFiles = [
      '/css/bootstrap.min.css',
      '/css/fontawesome.min.css',
      '/css/flaticon.css',
      '/css/magnific-popup.css',
      '/css/owl.carousel.min.css',
      '/css/owl.theme.default.min.css',
      '/css/slick.css',
      '/css/slick-theme.css',
      '/css/animate.css',
      '/css/spinner.css',
      '/css/style.css',
      '/css/responsive.css',
      '/assets/css/preloader.css',
      '/assets/css/meanmenu.css',
      '/assets/css/animate.min.css',
      '/assets/css/swiper-bundle.css',
      '/assets/css/huicalendar.css',
      '/assets/css/nice-select.css',
      '/assets/css/fontAwesome5Pro.css',
      '/assets/css/default.css'
    ];

    const loadedLinks = []; // Yüklenen link'leri takip et

    // CSS'lerin yüklenmesini bekle
    const loadCSS = (href) => {
      return new Promise((resolve, reject) => {
        // Zaten yüklü mü kontrol et
        const existingLink = document.querySelector(`link[href="${href}"]`);
        if (existingLink) {
          // Eğer zaten yüklüyse ve bizim işaretimiz yoksa işaretle
          if (!existingLink.hasAttribute('data-landing-page')) {
            existingLink.setAttribute('data-landing-page', 'true');
            loadedLinks.push(existingLink);
          }
          resolve();
          return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-landing-page', 'true'); // LandingPage için işaretle
        link.onload = () => {
          loadedLinks.push(link); // Yüklenen link'i kaydet
          resolve();
        };
        link.onerror = () => resolve(); // Hata olsa bile devam et
        document.head.appendChild(link);
      });
    };

    // Google Fonts yükleme
    const loadGoogleFont = (href) => {
      return new Promise((resolve) => {
        const existingLink = document.querySelector(`link[href="${href}"]`);
        if (existingLink) {
          if (!existingLink.hasAttribute('data-landing-page')) {
            existingLink.setAttribute('data-landing-page', 'true');
            loadedLinks.push(existingLink);
          }
          resolve();
          return;
        }
        const link = document.createElement('link');
        link.href = href;
        link.rel = 'stylesheet';
        link.setAttribute('data-landing-page', 'true'); // LandingPage için işaretle
        link.onload = () => {
          loadedLinks.push(link);
          resolve();
        };
        link.onerror = () => resolve();
        document.head.appendChild(link);
      });
    };

    // Tüm CSS'leri yükle
    Promise.all([
      ...cssFiles.map(loadCSS),
      loadGoogleFont('https://fonts.googleapis.com/css?family=Roboto:300,400,500,700,900'),
      loadGoogleFont('https://fonts.googleapis.com/css?family=Noto+Sans+TC:300,400,500,700,900'),
      loadGoogleFont('https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700&display=swap')
    ]).then(() => {
      // CSS'ler yüklendikten sonra kısa bir delay ile sayfayı göster (render için)
      setTimeout(() => {
        setCssLoaded(true);
      }, 100);
    });

    // Cleanup: Component unmount olduğunda CSS'leri ve class'ları kaldır
    return () => {
      // Cleanup başladığını işaretle - LoginPage ve LecturePage bunu görecek
      startCleanup();
      
      // LandingPage için yüklenen CSS'leri kaldır
      const landingPageLinks = document.querySelectorAll('link[data-landing-page="true"]');
      landingPageLinks.forEach(link => {
        link.remove();
      });
      
      // Body ve HTML class'larını kaldır
      document.body.classList.remove('landing-page-active');
      document.documentElement.classList.remove('landing-page-active');
      
      // CSS'lerin kaldırılmasını garanti etmek için bir frame bekle ve cleanup'ı tamamla
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          finishCleanup();
        });
      });
    };
  }, []);

  // jQuery ve diğer script'leri yükle (sadece bir kez)
  useEffect(() => {
    // Script'lerin zaten yüklü olup olmadığını kontrol et
    const isScriptsLoaded = document.querySelector('script[src="/js/jquery-3.3.1.min.js"]');
    if (isScriptsLoaded) {
      // Script'ler zaten yüklü, sadece WOW'u başlat
      if (window.WOW && !window.wowInitialized) {
        new window.WOW().init();
        window.wowInitialized = true;
      }
      return;
    }

    const scriptFiles = [
      '/js/jquery-3.3.1.min.js',
      '/js/bootstrap.min.js',
      '/js/fontawesome.min.js',
      '/js/modernizr.custom.js',
      '/js/jquery.easing.js',
      '/js/retina.js',
      '/js/jquery.appear.js',
      '/js/jquery.stellar.min.js',
      '/js/jquery.scrollto.js',
      '/js/imagesloaded.pkgd.min.js',
      '/js/isotope.pkgd.min.js',
      '/js/slick.min.js',
      '/js/owl.carousel.min.js',
      '/js/jquery.magnific-popup.min.js',
      '/js/jquery.validate.min.js',
      '/js/jquery.ajaxchimp.min.js',
      '/js/wow.js',
      '/js/custom.js',
      '/assets/js/swiper-bundle.min.js',
      '/assets/js/nice-select.min.js', // niceSelect için
      '/assets/js/imagesloaded.pkgd.min.js', // imagesLoaded için
      '/assets/js/isotope.pkgd.min.js', // isotope için
      '/assets/js/magnific-popup.min.js', // magnificPopup için
      '/assets/js/parallax.min.js', // parallax için
      '/assets/js/counterup.min.js', // counter için
      '/assets/js/meanmenu.js', // main.js'den önce yüklenmeli
      '/assets/js/main.js'
    ];

    // Script'leri sırayla yükle
    const loadScripts = async () => {
      for (const src of scriptFiles) {
        await new Promise((resolve, reject) => {
          // Script zaten yüklü mü kontrol et
          const existingScript = document.querySelector(`script[src="${src}"]`);
          if (existingScript) {
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.src = src;
          script.async = false;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
          document.body.appendChild(script);
        });
      }

      // Tüm script'ler yüklendikten sonra WOW'u başlat
      if (window.WOW && !window.wowInitialized) {
        new window.WOW().init();
        window.wowInitialized = true;
      }
    };

    loadScripts().catch(console.error);
  }, []);

  // Preloader - CSS'ler yüklendikten sonra
  useEffect(() => {
    if (!cssLoaded) return;

    const timer = setTimeout(() => {
      setLoading(false);
      // jQuery ile preloader'ı gizle
      if (window.$) {
        window.$('#loader').delay(100).fadeOut();
        window.$('#loader-wrapper').delay(100).fadeOut('fast');
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [cssLoaded]);

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 72) {
        setNavbarScrolled(true);
        if (window.$) {
          window.$('.navbar').addClass('scroll');
        }
      } else {
        setNavbarScrolled(false);
        if (window.$) {
          window.$('.navbar').removeClass('scroll');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll için anchor linkler
  useEffect(() => {
    const handleAnchorClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (anchor && anchor.getAttribute('href') !== '#') {
        const href = anchor.getAttribute('href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            const offsetTop = target.offsetTop - 110;
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });
          }
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
      return () => document.removeEventListener('click', handleAnchorClick);
    }, []);

  // Ücretsiz görüşme kayıt formu submit
  const handleConsultationFormSubmit = async (e) => {
    e.preventDefault();
    setConsultationFormError('');
    setConsultationFormLoading(true);

    try {
      // Validasyon
      if (!consultationForm.firstName.trim()) {
        setConsultationFormError('Lütfen adınızı girin');
        setConsultationFormLoading(false);
        return;
      }

      if (!consultationForm.email.trim()) {
        setConsultationFormError('Lütfen e-posta adresinizi girin');
        setConsultationFormLoading(false);
        return;
      }

      if (!consultationForm.phoneNumber.trim()) {
        setConsultationFormError('Lütfen telefon numaranızı girin');
        setConsultationFormLoading(false);
        return;
      }

      // Telefon numarası formatı kontrolü (basit)
      const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
      if (!phoneRegex.test(consultationForm.phoneNumber.trim())) {
        setConsultationFormError('Lütfen geçerli bir telefon numarası girin');
        setConsultationFormLoading(false);
        return;
      }

      if (!consultationForm.password || consultationForm.password.length < 6) {
        setConsultationFormError('Şifreniz en az 6 karakter olmalıdır');
        setConsultationFormLoading(false);
        return;
      }

      // Kullanıcı oluştur
      const credential = await createUserWithEmailAndPassword(
        auth,
        consultationForm.email.trim(),
        consultationForm.password
      );

      // Profil güncelle
      await updateProfile(credential.user, {
        displayName: consultationForm.firstName.trim()
      });

      // Firestore'da kullanıcı dökümanı oluştur (signupSource: 'consultation')
      await createUserDocument(
        credential.user.uid,
        consultationForm.email.trim(),
        consultationForm.firstName.trim(),
        consultationForm.phoneNumber.trim(),
        'consultation'
      );

      // Google Analytics event
      if (window.gtag) {
        window.gtag('event', 'consultation_signup', {
          event_category: 'engagement',
          event_label: 'Ücretsiz Görüşme Kaydı'
        });
      }

      setConsultationFormSuccess(true);
      setConsultationFormLoading(false);

    } catch (error) {
      console.error('Kayıt hatası:', error);
      let errorMessage = 'Kayıt sırasında bir hata oluştu';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Bu e-posta adresi zaten kullanılıyor';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Geçersiz e-posta adresi';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Şifre çok zayıf';
      }
      
      setConsultationFormError(errorMessage);
      setConsultationFormLoading(false);
    }
  };

  // CSS'ler yüklenene kadar preloader göster
  if (!cssLoaded) {
    return (
      <div id="loader-wrapper" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div id="loader" style={{
          width: '50px',
          height: '50px',
          border: '4px solid #333',
          borderTop: '4px solid #fff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      {/* PRELOADER SPINNER */}
      {loading && (
        <div id="loader-wrapper">
          <div id="loader">
            <div className="cssload-spinner"></div>
          </div>
        </div>
      )}

      {/* PAGE CONTENT */}
      <div id="page" className="page landing-page-container">
        {/* HEADER */}
        <header id="header" className="header">
          <nav style={{ zIndex: 1000 }} className={`fixed top-0 left-0 right-0 transition-all duration-300 ${
            navbarScrolled 
              ? 'bg-gray-900/95 backdrop-blur-sm shadow-lg' 
              : 'bg-transparent'
          }`}>
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-20">
                {/* Logo */}
                <div className="flex-shrink-0">
                    <img 
                    src="/images/logo_yasasinhamileyim_yazisiz.png" 
                      alt="Yaşasın Hamileyim" 
                      className="h-12 object-contain"
                    />
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-6">
                  {/* Giriş Yap / Profil Butonu */}
                  {user ? (
                    <Link 
                      to="/profile" 
                      className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 flex items-center gap-2"
                    >
                      <User className="w-5 h-5" />
                      Profil
                    </Link>
                  ) : (
                    <Link 
                      to="/login" 
                      className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30"
                    >
                      Giriş yap
                    </Link>
                  )}
                  
                  {/* Sepet İkonu - Commented out */}
                  {/*
                  <button
                    className={`relative p-2 transition-colors duration-200 ${
                      navbarScrolled ? 'text-white hover:text-purple-400' : 'text-gray-900 hover:text-purple-600'
                    }`}
                    aria-label="Sepet"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      0
                    </span>
                  </button>
                  */}
                </div>

                {/* Mobile Giriş Yap / Profil Button */}
                  {user ? (
                    <Link 
                      to="/profile" 
                      className="md:hidden bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 text-base flex items-center gap-2"
                    >
                      <User className="w-5 h-5" />
                      Profil
                    </Link>
                  ) : (
                    <Link 
                      to="/login" 
                      className="md:hidden bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 text-base"
                    >
                      Giriş yap
                    </Link>
                  )}
              </div>
            </div>
          </nav>
        </header>

        {/* HERO-15 */}
        <section id="hero-15" className="hero-section division" style={{
          background: 'linear-gradient(135deg, #f8b4d9 0%, #e8a5d4 25%, #d896cf 50%, #c787ca 75%, #b678c5 100%)',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: '120px',
          paddingBottom: '80px',
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center'
        }}>
          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '600px',
            height: '600px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 1
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '400px',
            height: '400px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            zIndex: 1
          }}></div>

          <div className="container" style={{ position: 'relative', zIndex: 2 }}>
            <div className="row align-items-center">
              {/* HERO TEXT */}
              <div className="col-md-6 col-lg-6">
                <div className="hero-txt wow fadeInUp" data-wow-duration="0.8s" data-wow-delay="0.2s">
                  <h1 style={{
                    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                    fontWeight: '700',
                    lineHeight: '1.2',
                    color: '#ffffff',
                    marginBottom: '24px',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    fontFamily: "'Mulish', sans-serif"
                  }}>
                    Yaşasın Hamileyim!
                  </h1>
                  <p className="p-lg" style={{
                    fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
                    fontWeight: '400',
                    lineHeight: '1.8',
                    color: '#ffffff',
                    marginBottom: '32px',
                    textShadow: '0 1px 5px rgba(0, 0, 0, 0.1)',
                    fontFamily: "'Mulish', sans-serif"
                  }}>
                    Hayatınızın en değerli yolculuğunu <strong style={{ fontWeight: '600', color: '#ffd89b' }}>güvende ve rahat</strong> geçirmeniz için yanınızdayız!
                  </p>
                  
                  {/* 1000+ Anne Tecrübesi - Eğri Çizgi ile */}
                  <div style={{ 
                    display: 'inline-block',
                    marginTop: '20px',
                    position: 'relative'
                  }}>
                    <span style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#1a1a1a',
                      fontFamily: "'Mulish', sans-serif",
                      letterSpacing: '0.5px',
                      position: 'relative',
                      display: 'inline-block',
                      paddingBottom: '12px',
                      zIndex: 2
                    }}>
                      1000+ Anne Tecrübesi
                      {/* Eğri Çizgi SVG */}
                      <svg 
                        style={{
                          position: 'absolute',
                          bottom: '0',
                          left: '-5%',
                          width: '110%',
                          height: '15px',
                          zIndex: -1,
                          overflow: 'visible'
                        }}
                        viewBox="0 0 220 15"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M 0 10 Q 55 2, 110 8 T 220 10"
                          stroke="#ffd700"
                          strokeWidth="4"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </div>
                  
                  {/* Premium Üyeler İçin "Eğitime Devam Et" Butonu */}
                  {user && userData?.isPremium && (
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '25px' }}>
                    <Link
                        to="/lectures"
                      className="btn btn-md btn-primary tra-white-hover"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: '#ffffff',
                        padding: '14px 32px',
                        borderRadius: '50px',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                        textDecoration: 'none',
                        display: 'inline-block',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(10px)'
                      }}
                      onClick={() => {
                        // Google Analytics event tracking (GA4)
                        if (window.gtag) {
                            window.gtag('event', 'egitime_devam_et_button_click', {
                            event_category: 'engagement',
                              event_label: 'Premium Üyeler İçin Eğitime Devam Et Butonu',
                              button_name: 'Premium Üyeler İçin Eğitime Devam Et',
                              button_location: 'Hero Section - 1000+ Anne Tecrübesi'
                          });
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                    >
                        Eğitime Devam Et
                    </Link>
                  </div>
                  )}
                </div>
              </div>

              {/* HERO IMAGE */}
              <div className="col-md-6 col-lg-6">
                <div className="hero-15-img text-center wow fadeInUp" data-wow-duration="0.8s" data-wow-delay="0.4s" style={{
                  position: 'relative',
                  paddingTop: '20px'
                }}>
                  <div style={{
                    position: 'relative',
                    display: 'inline-block',
                    filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.2))'
                  }}>
                    <img
                      className="img-fluid"
                      src="/images/welcome_dikey_750.png"
                      alt="Yaşasın Hamileyim"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '10px',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                </div>
              </div>
            </div>
          </div>
          </div>
          
          {/* Bottom Wave */}
          <div className="hero-bottom-wave" style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60px',
            background: '#f8f9fb',
            clipPath: 'polygon(0 60px, 100% 0, 100% 100%, 0 100%)',
            zIndex: 2
          }}></div>
        </section>

        {/* PROBLEM AWARENESS SECTION - Hamilelikte Tek Başınıza Değilsiniz */}
        <section ref={info2SectionRef} id="problem-awareness" className="problem-awareness-section wide-60 info-section division" style={{
          backgroundColor: '#f8f9fb'
        }}>
          <div className="container">
            <div className="row d-flex align-items-center">
              {/* SOL: GÖRSEL - Daha büyük */}
              <div className="col-md-7 col-lg-8 mb-0 wow fadeInLeft" data-wow-duration="1s">
                <div className="img-block pr-md-30" style={{ marginBottom: '0' }}>
                  <img 
                    src="/images/hamile_soru_isaretleri_transparent_nobg_1046_830.png" 
                    alt="Hamilelikte kafası karışık anne - Bilgi bombardımanı"
                    className="img-fluid"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      marginBottom: '0'
                    }}
                  />
                </div>
              </div>

              {/* SAĞ: İÇERİK - Daha kompakt ve vertical */}
              <div className="col-md-5 col-lg-4">
                <div className="txt-block wow fadeInRight" data-wow-duration="1s" style={{
                      display: 'flex',
                  flexDirection: 'column',
                      justifyContent: 'center',
                  height: '100%',
                  paddingLeft: '20px'
                }}>
                  {/* Dekoratif Accent Line */}
                  <div style={{
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, #c084fc 0%, #f472b6 100%)',
                    borderRadius: '2px',
                    marginBottom: '24px'
                  }}></div>

                  {/* Başlık */}
                  <h2 style={{
                    fontSize: 'clamp(32px, 4vw, 48px)',
                    fontWeight: '800',
                    color: '#1a1a1a',
                    marginBottom: '24px',
                    fontFamily: "'Mulish', sans-serif",
                    lineHeight: '1.15',
                    letterSpacing: '-0.5px'
                  }}>
                    <span style={{
                      background: 'linear-gradient(90deg, #c084fc 0%, #f472b6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>Yalnız Değilsiniz</span>
                  </h2>

                  {/* Alt Metin - İki Cümle */}
                  <div style={{
                    color: '#4a4a4a',
                    fontFamily: "'Mulish', sans-serif"
                  }}>
                    <p style={{ 
                      fontSize: 'clamp(18px, 2vw, 22px)',
                      lineHeight: '1.6',
                      marginBottom: '10px',
                      fontWeight: '400'
                    }}>
                      Herkesten farklı şeyler duyuyorsun.
                    </p>
                    <p style={{ 
                      fontSize: 'clamp(18px, 2vw, 22px)',
                      lineHeight: '1.6',
                      marginBottom: '0',
                      fontWeight: '600',
                      color: '#2d2d2d'
                    }}>
                      Peki uzmanlardan doğru bilgiye ulaşmak ister misin?
                    </p>
                  </div>

                  {/* Dekoratif Accent Line - Alt */}
                  <div style={{
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, #c084fc 0%, #f472b6 100%)',
                    borderRadius: '2px',
                    marginTop: '24px',
                    marginLeft: 'auto'
                  }}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM SECTION - Bilgi Karmaşasının Bedeli */}
        <section className="problem-section pt-100 pb-100 division" style={{
          backgroundColor: '#ffffff'
        }}>
          <div className="container">
            {/* Başlık */}
            <div className="row">
              <div className="col-12">
                <div className="text-center mb-60">
                  <h2 style={{
                    fontSize: 'clamp(32px, 4vw, 44px)',
                    fontWeight: '800',
                    color: '#1a1a1a',
                    marginBottom: '0',
                    fontFamily: "'Mulish', sans-serif"
                  }}>
                    Çoğu anne adayı
                  </h2>
                </div>
              </div>
            </div>

            {/* Problem Listesi - Grid Layout */}
            <div className="row" style={{ marginBottom: '50px' }}>
              {/* Problem 1 */}
              <div className="col-12 col-md-6 mb-3">
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '25px',
                  height: '100%',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderLeft: '4px solid #ff6b6b',
                  borderRadius: '8px',
                      transition: 'all 0.3s ease',
                  cursor: 'default'
                    }}
                    onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <span style={{ 
                    fontSize: '32px', 
                    flexShrink: 0,
                    lineHeight: '1'
                  }}>❗</span>
                  <p style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#2d2d2d',
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: '500'
                  }}>
                    Lohusalık depresyonu yaşıyor
                  </p>
                </div>
              </div>

              {/* Problem 2 */}
              <div className="col-12 col-md-6 mb-3">
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '25px',
                  height: '100%',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderLeft: '4px solid #ff8c42',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <span style={{ 
                    fontSize: '32px', 
                    flexShrink: 0,
                    lineHeight: '1'
                  }}>❗</span>
                  <p style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#2d2d2d',
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: '500'
                  }}>
                    Hamilelikte bel ve sırt ağrısı çekiyor
                  </p>
                </div>
              </div>

              {/* Problem 3 */}
              <div className="col-12 col-md-6 mb-3">
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '25px',
                  height: '100%',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderLeft: '4px solid #ffa07a',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <span style={{ 
                    fontSize: '32px', 
                    flexShrink: 0,
                    lineHeight: '1'
                  }}>❗</span>
                  <p style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#2d2d2d',
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: '500'
                  }}>
                    Bebeği nasıl emzireceğini bilmiyor
                  </p>
                </div>
              </div>

              {/* Problem 4 */}
              <div className="col-12 col-md-6 mb-3">
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '25px',
                  height: '100%',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderLeft: '4px solid #ff9999',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <span style={{ 
                    fontSize: '32px', 
                    flexShrink: 0,
                    lineHeight: '1'
                  }}>❗</span>
                  <p style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#2d2d2d',
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: '500'
                  }}>
                    Doğuma kendini hazır hissetmiyor
                  </p>
            </div>
          </div>

              {/* Problem 5 */}
              <div className="col-12 col-md-6 mb-3">
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '25px',
                  height: '100%',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderLeft: '4px solid #ffb3b3',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
        }}>
                  <span style={{ 
                    fontSize: '32px', 
                    flexShrink: 0,
                    lineHeight: '1'
                  }}>❗</span>
                  <p style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#2d2d2d',
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: '500'
                  }}>
                    Hamilelikte nasıl beslenmesi gerektiğini bilmiyor
                  </p>
                </div>
              </div>

              {/* Problem 6 */}
              <div className="col-12 col-md-6 mb-3">
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '25px',
                  height: '100%',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderLeft: '4px solid #ffcccc',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <span style={{ 
                    fontSize: '32px', 
                    flexShrink: 0,
                    lineHeight: '1'
                  }}>❗</span>
                  <p style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#2d2d2d',
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: '500'
                  }}>
                    Bebek bakımında kendini yetersiz hissediyor
                  </p>
                </div>
              </div>
            </div>

            {/* Çözüm Mesajı */}
            <div className="row">
              <div className="col-12 col-lg-10 offset-lg-1">
                <div style={{
                  textAlign: 'center',
                  padding: '35px',
                  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  borderRadius: '12px',
                  border: '2px solid #667eea30'
                }}>
                  <p style={{ 
                    fontSize: '22px', 
                    fontWeight: '600',
                    color: '#2d2d2d',
                    margin: 0,
                    fontFamily: "'Mulish', sans-serif",
                    lineHeight: '1.6'
                  }}>
                    <span style={{ fontSize: '34px', marginRight: '12px', color: '#22c55e', fontWeight: 'bold' }}>✓</span>
                    Doğru bilgi ile bunların hepsinin önüne geçebilirsiniz.
                    </p>
                  </div>
                </div>
              </div>
            </div>
        </section>

        {/* SOLUTION VIDEO SECTION - Full Width */}
        <section className="solution-video-section" style={{
          padding: '0',
          margin: '0',
          backgroundColor: '#ffffff',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            position: 'relative'
          }}>
            <video
              ref={(el) => {
                if (el) {
                  // Video oynat
                  const playVideo = () => {
                    el.play().catch(err => {
                      console.log('Video autoplay engellendi:', err);
                    });
                  };
                  
                  // Video zaman güncellemelerini dinle
                  const handleTimeUpdate = () => {
                    const currentTime = el.currentTime;
                    
                    // Zaman aralıklarına göre başlıkları güncelle
                    if (currentTime >= 0 && currentTime < 5) {
                      setVideoOverlayText('Bel ağrısı neden olur? Nasıl önlenir?');
                    } else if (currentTime >= 5 && currentTime < 22) {
                      setVideoOverlayText('Gebelikte nasıl beslenmeliyiz?');
                    } else if (currentTime >= 22 && currentTime < 30) {
                      setVideoOverlayText('Emzirmede yaşanabilecek sorunlar ve çözümleri');
                    } else if (currentTime >= 30 && currentTime < 40) {
                      setVideoOverlayText('Şeker yükleme testinden korkmalı mıyım?');
                    } else {
                      setVideoOverlayText('');
                    }
                  };
                  
                  el.addEventListener('timeupdate', handleTimeUpdate);
                  
                  // Video görünür olunca oynat
                  if ('IntersectionObserver' in window) {
                    const observer = new IntersectionObserver((entries) => {
                      entries.forEach(entry => {
                        if (entry.isIntersecting) {
                          playVideo();
                          observer.unobserve(el);
                        }
                      });
                    }, {
                      threshold: 0.1 // Video %10 görünür olunca başlat
                    });
                    observer.observe(el);
                  } else {
                    // IntersectionObserver desteklenmiyorsa direkt başlat
                    setTimeout(playVideo, 1000);
                  }
                  
                  // Cleanup
                  return () => {
                    el.removeEventListener('timeupdate', handleTimeUpdate);
                  };
                }
              }}
              muted
              playsInline
              loop
              preload="auto"
              poster="/images/video_thumbnail.jpg"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover'
              }}
            >
              <source 
                src="https://player.vimeo.com/progressive_redirect/playback/1156829250/rendition/1080p/file.mp4%20%281080p%29.mp4?loc=external&signature=9de985215cd9329e65d466012a601b40d6fa8beffc171f7c1da82406f88b92d0" 
                type="video/mp4" 
              />
              Tarayıcınız video etiketini desteklemiyor.
            </video>

            {/* Video Overlay Text */}
            {videoOverlayText && (
              <div className="video-overlay-text" style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0',
                width: '100%',
                textAlign: 'center',
                zIndex: 10,
                background: 'linear-gradient(to top, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0) 100%)',
                animation: 'fadeInUp 0.6s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '15px'
              }}>
                <h2 className="video-overlay-title" style={{
                  fontSize: videoOverlayText.includes('Emzirmede') 
                    ? 'clamp(18px, 3vw, 34px)'  // Emzirme yazısı daha küçük
                    : 'clamp(18px, 3.5vw, 42px)', // Diğer yazılar normal
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #c084fc 0%, #f472b6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontFamily: "'Mulish', sans-serif",
                  lineHeight: '1.3',
                  margin: 0,
                  letterSpacing: '-0.5px',
                  padding: '0 20px',
                  maxWidth: '900px'
                }}>
                  {videoOverlayText}
                </h2>
                
                {/* Eğitime Git Butonu - Sadece Desktop'ta yazının altında */}
                <Link
                  to={userData?.isPremium ? "/lectures" : "/ornek-videolar"}
                  className="video-overlay-button-desktop"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: 'clamp(10px, 2vw, 14px) clamp(24px, 4vw, 32px)',
                    background: 'linear-gradient(135deg, #c084fc 0%, #f472b6 100%)',
                    color: '#ffffff',
                    borderRadius: '50px',
                    fontWeight: '600',
                    fontSize: 'clamp(14px, 2.5vw, 18px)',
                    textDecoration: 'none',
                    fontFamily: "'Mulish', sans-serif",
                    boxShadow: '0 4px 15px rgba(192, 132, 252, 0.4)',
                    transition: 'all 0.3s ease',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    // Google Analytics event tracking (GA4)
                    if (window.gtag) {
                      window.gtag('event', userData?.isPremium ? 'video_overlay_egitime_git_click' : 'video_overlay_ornek_videolar_click', {
                        event_category: 'engagement',
                        event_label: 'Video Overlay - Eğitime Git Butonu',
                        button_location: 'Solution Video Section',
                        is_premium: userData?.isPremium || false
                      });
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(192, 132, 252, 0.6)';
                    const arrow = e.currentTarget.querySelector('svg');
                    if (arrow) arrow.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(192, 132, 252, 0.4)';
                    const arrow = e.currentTarget.querySelector('svg');
                    if (arrow) arrow.style.transform = 'translateX(0)';
                  }}
                >
                  <span>Eğitime Git</span>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    style={{ 
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <path 
                      d="M6 3L11 8L6 13" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            )}

            {/* Eğitime Git Butonu - Mobilde Video Sağ Üstte */}
            <Link
              to={userData?.isPremium ? "/lectures" : "/ornek-videolar"}
              className="video-overlay-button-mobile"
              style={{
                display: 'none', // Mobilde CSS ile gösterilecek
                position: 'absolute',
                top: '15px',
                right: '15px',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #c084fc 0%, #f472b6 100%)',
                color: '#ffffff',
                borderRadius: '50px',
                fontWeight: '600',
                fontSize: '13px',
                textDecoration: 'none',
                fontFamily: "'Mulish', sans-serif",
                boxShadow: '0 4px 15px rgba(192, 132, 252, 0.5)',
                transition: 'all 0.3s ease',
                border: 'none',
                cursor: 'pointer',
                zIndex: 20
              }}
              onClick={() => {
                // Google Analytics event tracking (GA4)
                if (window.gtag) {
                  window.gtag('event', userData?.isPremium ? 'video_overlay_egitime_git_mobile_click' : 'video_overlay_ornek_videolar_mobile_click', {
                    event_category: 'engagement',
                    event_label: 'Video Overlay Mobile - Eğitime Git Butonu',
                    button_location: 'Solution Video Section - Mobile Top Right',
                    is_premium: userData?.isPremium || false
                  });
                }
              }}
            >
              <span>Eğitime Git</span>
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 16 16" 
                fill="none"
              >
                <path 
                  d="M6 3L11 8L6 13" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </section>

        {/* Video Overlay Animation & Styles */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Video Overlay Responsive Padding */
          .video-overlay-text {
            padding: 25px 20px;
          }

          @media (max-width: 768px) {
            .video-overlay-text {
              padding: 20px 15px;
              gap: 10px;
              bottom: -1px !important;
            }
            
            /* Mobilde yazı daha küçük */
            .video-overlay-title {
              font-size: clamp(16px, 4vw, 20px) !important;
              line-height: 1.25 !important;
            }
            
            /* Desktop butonu mobilde gizle */
            .video-overlay-button-desktop {
              display: none !important;
            }
            
            /* Mobil butonu göster - video sağ üstünde */
            .video-overlay-button-mobile {
              display: inline-flex !important;
            }
          }

          @media (max-width: 480px) {
            .video-overlay-text {
              padding: 15px 10px;
              gap: 8px;
            }
            
            /* Çok küçük ekranlarda yazı daha da küçük */
            .video-overlay-title {
              font-size: 15px !important;
              line-height: 1.2 !important;
            }
            
            /* Çok küçük ekranlarda buton daha da küçük */
            .video-overlay-button-mobile {
              top: 10px !important;
              right: 10px !important;
              padding: 6px 12px !important;
              font-size: 12px !important;
              gap: 4px !important;
            }
            
            .video-overlay-button-mobile svg {
              width: 10px !important;
              height: 10px !important;
            }
          }
        `}</style>

        {/* Custom Padding Styles */}
        <style>{`
          /* Eğitmenlerimiz Başlık Margin */
          .egitmenler-baslik {
            margin-top: 50px;
          }
          
          @media (min-width: 992px) {
            .egitmenler-baslik {
              margin-top: 70px;
            }
          }
          
          /* Problem Awareness Section Padding */
          .problem-awareness-section {
            padding: 40px 0 0 0 !important;
          }
          
          .problem-awareness-section .img-block {
            margin-bottom: 0 !important;
          }
          
          .problem-awareness-section .col-md-7,
          .problem-awareness-section .col-lg-8 {
            margin-bottom: 0 !important;
          }
          
          @media (max-width: 991px) {
            .problem-awareness-section {
              padding: 20px 0 0 0 !important;
            }
          }
          
          /* Consultation Section Padding */
          .consultation-section {
            padding-top: 80px !important;
          }
          
          @media (max-width: 991px) {
            .consultation-section {
              padding-top: 40px !important;
            }
          }
        `}</style>

        {/* Eğitmenlerimiz */}
        <section ref={info4SectionRef} id="info-4" className="info-4-row pt-100 info-section division" style={{ paddingTop: '0px' }}>
          <div className="pb-100 bg-inner bg-lightgrey division">
            <div className="container">
              {/* Section Header */}
              <div className="row">
                <div className="col-lg-10 offset-lg-1">
                  <div className="section-title text-center mb-60">
                    <h2 className="h2-lg egitmenler-baslik">Eğitmenlerimiz</h2>
                  </div>
                </div>
              </div>

              {/* Eğitmen Kartları */}
              <div className="row d-flex align-items-center justify-content-center">
                {/* Eğitmen 1 - Dr. Emre Uysal */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/emre_uysal_egitmenlerimiz.jpeg" alt="Dr. Emre Uysal" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Op. Dr. Emre Uysal</h4>
                    <p className="p-md text-muted">Kadın Hastalıkları ve Doğum Uzmanı</p>
                  </div>
                </div>

                {/* Eğitmen 2 - Dr. Serhat Özdemir */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.1s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/serhat_ozdemir_square.jpeg" alt="Dr. Serhat Özdemir" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Dr. Serhat Özdemir</h4>
                    <p className="p-md text-muted">Çocuk Sağlığı ve Hastalıkları Uzmanı</p>
                  </div>
                </div>

                {/* Eğitmen 3 - Ayşenur Yıldız */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.2s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/ebe_aysenur_yildiz.jpeg" alt="Ayşenur Yıldız" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Ayşenur Yıldız</h4>
                    <p className="p-md text-muted">Ebe ve Emzirme Danışmanı</p>
                  </div>
                </div>

                {/* Eğitmen 3 - Begüm Derici Ülker */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.3s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/begum_derici_ulker.jpeg" alt="Begüm Derici Ülker" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Begüm Derici Ülker</h4>
                    <p className="p-md text-muted">Klinik Psikolog</p>
                  </div>
                </div>

                {/* Eğitmen 4 - Hilal (Diyetisyen) */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.4s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/diyetisyen_hilal.jpeg" alt="Hilal" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Hilal Yılmaz Sakallı</h4>
                    <p className="p-md text-muted">Diyetisyen</p>
                  </div>
                </div>

                {/* Eğitmen 5 - Hilal Dinçer */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.5s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/pilates_hilal_square.jpeg" alt="Eda Dinçer" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Eda Dinçer</h4>
                    <p className="p-md text-muted">Pilates Eğitmeni</p>
                  </div>
                </div>

                {/* Eğitmen 6 - Dr. Uğur Özel */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.6s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/anestezi_ugur_ozel.jpeg" alt="Dr. Uğur Özel" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Dr. Uğur Özel</h4>
                    <p className="p-md text-muted">Anestezi Uzmanı</p>
                  </div>
                </div>

                {/* Eğitmen 7 - Sinem Göz */}
                <div className="col-md-6 col-lg-4 mb-40">
                  <div className="txt-block text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.7s">
                    <div className="img-block mb-25">
                      <img className="img-fluid rounded-circle" src="/images/egitmenler/nefes_uzmani_sinem_goz.jpeg" alt="Sinem Göz" style={{ width: '200px', height: '200px', objectFit: 'cover', margin: '0 auto' }} />
                    </div>
                    <h4 className="h4-md">Sinem Göz</h4>
                    <p className="p-md text-muted">Nefes Koçu</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* Kullanıcı Yorumları */}
        {/*<section className="testimonials-section pt-100 pb-100 division" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>*/}
        <section className="testimonials-section pt-100 pb-100 division" style={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #fce4ec 100%)' }}>
          <div className="container">
            {/* Section Header */}
            <div className="row">
              <div className="col-lg-10 offset-lg-1">
                <div className="section-title text-center mb-60">
                  <h2 className="h2-lg" style={{ fontFamily: "'Mulish', sans-serif", color: '#333' }}>Annelerimizden Gelen Yorumlar</h2>
                  <p className="p-lg" style={{ color: '#666', marginTop: '15px' }}>1000+ annenin güvendiği eğitim</p>
                </div>
              </div>
            </div>

            {/* Yorumlar */}
            <div className="row">
              {/* Yorum 1 */}
              <div className="col-md-6 col-lg-4 mb-30">
                <div className="testimonial-card wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0s" style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <span style={{ color: '#ffd700', fontSize: '20px' }}>★★★★★</span>
                  </div>
                  <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px', flexGrow: 1 }}>
                    Emzirme problemi yaşadığım sırada araştırırken karşıma çıktı, iyiki de çıkmış :) Eğitimde öğrendiklerim sayesinde sarılık olmaktan son anda kurtulduk, emekleriniz için teşekkür ederim 🙏
                  </p>
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>Zeynep Ş.</p>
                  </div>
                </div>
              </div>

              {/* Yorum 2 */}
              <div className="col-md-6 col-lg-4 mb-30">
                <div className="testimonial-card wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.1s" style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <span style={{ color: '#ffd700', fontSize: '20px' }}>★★★★★</span>
                  </div>
                  <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px', flexGrow: 1 }}>
                    İlk günden 20 videoyu bitirdim bile, konu sıralaması ve akış çok hoşuma gitti. Eğitimi tamamladığımda tekrar yazacağım. 😍
                  </p>
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>Ayşe K.</p>
                  </div>
                </div>
              </div>

              {/* Yorum 3 */}
              <div className="col-md-6 col-lg-4 mb-30">
                <div className="testimonial-card wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.2s" style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <span style={{ color: '#ffd700', fontSize: '20px' }}>★★★★★</span>
                  </div>
                  <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px', flexGrow: 1 }}>
                    Kafamdaki soru işaretlerinin çoğu silindi, telefondan da izledim ama asıl bilgisayardan izlemenizi tavsiye ederim 4K kalitesi ile görseller çok daha iyi.
                  </p>
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>Elif M.</p>
                  </div>
                </div>
              </div>

              {/* Yorum 4 */}
              <div className="col-md-6 col-lg-4 mb-30">
                <div className="testimonial-card wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.3s" style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <span style={{ color: '#ffd700', fontSize: '20px' }}>★★★★★</span>
                  </div>
                  <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px', flexGrow: 1 }}>
                    Hamileliğimin 26. haftasında arkadaşımın tavsiyesi ile aldım eğitimi. keşke daha önce haberim olsaydı. eğitim içeriği çok iyi, eksik olarak uyku eğitimi de eklenebilir.
                  </p>
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>Fatma K.</p>
                  </div>
                </div>
              </div>

              {/* Yorum 5 */}
              <div className="col-md-6 col-lg-4 mb-30">
                <div className="testimonial-card wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.4s" style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <span style={{ color: '#ffd700', fontSize: '20px' }}>★★★★★</span>
                  </div>
                  <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px', flexGrow: 1 }}>
                    Çocuk odasından, doğum çantasında ne olmalıya kadar her şeye değinilmiş. Doğum tüyoları videosu çok hoşuma gitti, umarım paniğe kapılmadan söylenenleri uygulayabilirim 🙏☺️
                  </p>
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>Merve D.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Paket / Fiyatlandırma */}
        <section id="pricing" className="pricing-section pt-100 pb-100 info-section division">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 col-xl-7">
                {/* Pricing Card */}
                <div ref={pricingCardRef} className="pricing-card text-center wow fadeInUp" data-wow-duration="0.7s" data-wow-delay="0.1s" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '20px',
                  padding: '50px 40px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  color: 'white'
                }}>
                  {/* Paket Başlığı */}
                  <h3 className="h3-lg mb-20" style={{ color: 'white', fontSize: '2rem' }}>Yaşasın Hamileyim Eğitimi</h3>

                  {/* Özel Avantajlar */}
                  <div className="pricing-benefits mb-30" style={{
                    marginBottom: '30px',
                    textAlign: 'center'
                  }}>
                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      alignItems: 'center'
                    }}>
                      <li style={{ 
                        fontSize: '1.3rem', 
                        color: '#ffd700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600'
                      }}>
                        <span>Sınırsız izleme hakkı</span>
                      </li>
                      <li style={{ 
                        fontSize: '1.3rem', 
                        color: '#ffd700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600'
                      }}>
                        <span>1 hafta içinde iade hakkı</span>
                      </li>
                      <li style={{ 
                        fontSize: '1.3rem', 
                        color: '#ffd700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600'
                      }}>
                        <span>9 taksite varan ödeme imkanı</span>
                      </li>
                    </ul>
                  </div>

                  {/* Özellikler */}
                  <div className="pricing-features mb-40" style={{ textAlign: 'left' }}>
                    <h5 className="h5-md mb-20" style={{ color: 'white', textAlign: 'center' }}>Paket İçeriği</h5>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {[
                        '6-40. Hafta Gebelik Kılavuzu',
                        'Doğuma Hazırlık Eğitimi',
                        'Bebek Sağlığı ve Bakımı',
                        'Emzirme Eğitimi',
                        'Hamilelikte Beslenme',
                        'Hamileliğe Psikolojik Yaklaşım',
                        'Hamile Pilatesi',
                        'Hamilelikte Nefes',
                        'Doğumda Anestezi',
                        'Yurtdışında Doğum',
                        'Oda Süsleme ve Doğum Fotoğrafçılığı Bilgilendirme'
                      ].slice(0, showAllFeatures ? 11 : 6).map((feature, index) => (
                        <li key={index} style={{ padding: '10px 0', fontSize: '1.25rem', lineHeight: '1.4', display: 'flex', alignItems: 'center' }}>
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" style={{ width: '20px', height: '20px', marginRight: '10px', flexShrink: 0 }}>
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                      {!showAllFeatures && (
                        <li 
                          onClick={() => {
                            setShowAllFeatures(true);
                            // Google Analytics event tracking (GA4)
                            if (window.gtag) {
                              window.gtag('event', 'daha_fazlasi_button_click', {
                                event_category: 'engagement',
                                event_label: 'Daha Fazlası Butonu',
                                button_location: 'Pricing Section'
                              });
                            }
                          }}
                          style={{ 
                            padding: '10px 0', 
                            fontSize: '1.25rem', 
                            lineHeight: '1.4', 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: '#ffd700',
                            transition: 'color 0.2s',
                            fontWeight: '600',
                            fontStyle: 'italic'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ffed4e';
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#ffd700';
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          <span style={{ marginLeft: '30px' }}>… ve daha fazlası</span>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Butonlar */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
                    {/* Eğitim Sayfasına Git Butonu */}
                    <Link
                      to={userData?.isPremium ? "/lectures" : "/ornek-videolar"}
                      style={{ textDecoration: 'none', width: '100%' }}
                    >
                      <button className="btn btn-lg" style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        fontWeight: '600',
                        padding: '15px 40px',
                        borderRadius: '50px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                        width: '100%',
                        backdropFilter: 'blur(10px)'
                      }}
                      onClick={() => {
                        // Google Analytics event tracking (GA4)
                        if (window.gtag) {
                          window.gtag('event', 'satin_alma_kismi_egitime_git', {
                            event_category: 'engagement',
                            event_label: 'Satın Alma Kısmı - Eğitim Sayfasına Git Butonu',
                            button_name: 'Eğitim Sayfasına Git',
                            button_location: 'Pricing Section'
                          });
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.3)';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.2)';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                      }}
                      >
                        Eğitim Sayfasına Git
                      </button>
                    </Link>

                  {/* Satın Al Butonu */}
                    <Link to="/checkout" style={{ textDecoration: 'none', width: '100%' }}>
                    <button className="btn btn-lg btn-white" style={{
                        background: 'linear-gradient(135deg, #4ade80 0%, #34d399 100%)',
                        color: '#ffffff',
                      fontWeight: 'bold',
                      padding: '15px 40px',
                      borderRadius: '50px',
                      border: 'none',
                      fontSize: '1.1rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                        boxShadow: '0 5px 15px rgba(74, 222, 128, 0.25)',
                      width: '100%'
                    }}
                    onClick={() => {
                      // Google Analytics event tracking (GA4)
                      if (window.gtag) {
                        window.gtag('event', 'satin_al_button_click', {
                          event_category: 'engagement',
                          event_label: 'Satın Al Butonu',
                          button_name: 'Satın Al',
                          button_location: 'Pricing Section'
                        });
                      }

                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 20px rgba(74, 222, 128, 0.35)';
                        e.target.style.background = 'linear-gradient(135deg, #34d399 0%, #22c55e 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 5px 15px rgba(74, 222, 128, 0.25)';
                        e.target.style.background = 'linear-gradient(135deg, #4ade80 0%, #34d399 100%)';
                    }}
                    >
                      Satın Al
                    </button>
                  </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER-2 */}
        <footer id="footer-2" className="wide-50 footer division">
          <div className="container">
            {/* FOOTER CONTENT */}
            <div className="row">
              {/* FOOTER LINKS */}
              <div className="col-md-4 col-lg-3">
                <div className="footer-links">
                  <h5 className="h5-sm">Sözleşmeler</h5>
                  <ul className="foo-links clearfix">
                    <li><p><a href="/teslimat-ve-iade" target="_blank" rel="noopener noreferrer">Teslimat ve İade Koşulları</a></p></li>
                    <li><p><a href="/gizlilik-sozlesmesi" target="_blank" rel="noopener noreferrer">Gizlilik Sözleşmesi</a></p></li>
                    <li><p><a href="/mesafeli-satis-sozlesmesi" target="_blank" rel="noopener noreferrer">Mesafeli Satış Sözleşmesi</a></p></li>
                  </ul>
                </div>
              </div>

              {/* FOOTER CONTACT & SOCIAL */}
              <div className="col-md-4 col-lg-4">
                <div className="footer-links">
                  <h5 className="h5-sm">İletişim</h5>
                  <ul className="foo-links clearfix">
                    <li>
                      <p>
                        <a href="tel:+905327757616" style={{ textDecoration: 'none', color: 'inherit' }}>
                          0 (532) 775 76 16
                        </a>
                      </p>
                    </li>
                    <li>
                      <p>
                        <a 
                          href="https://www.instagram.com/yasasinhamileyim/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'inherit' }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          Instagram
                        </a>
                      </p>
                    </li>
                  </ul>
                </div>
              </div>

              {/* FOOTER PAYMENT LOGOS - 3. Sütun */}
              <div className="col-md-4 col-lg-5">
                <div className="footer-payment-logos mb-25" style={{ textAlign: 'right' }}>
                  <img 
                    src="/images/iyzico-logo-pack/footer_iyzico_ile_ode/Colored/logo_band_colored.svg" 
                    alt="Ödeme Yöntemleri" 
                    style={{ maxWidth: '100%', height: 'auto', maxHeight: '60px' }}
                  />
                </div>
              </div>
            </div>

            {/* BOTTOM FOOTER */}
            <div className="bottom-footer">
              <div className="row">
                <div className="col-md-12">
                  <div className="footer-copyright">
                    <p className="p-sm">&copy; 2025 Yaşasın Hamileyim. Tüm hakları saklıdır.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* WhatsApp Button */}
        <a
          href="https://wa.me/905327757616?text=Eğitim hakkında daha fazla bilgi almak istiyorum"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '70px',
            height: '70px',
            backgroundColor: '#25D366',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
            zIndex: 999,
            transition: 'all 0.3s ease',
            textDecoration: 'none'
          }}
          onClick={() => {
            // Google Analytics event tracking (GA4)
            if (window.gtag) {
              window.gtag('event', 'whatsapp_button_click', {
                event_category: 'engagement',
                event_label: 'WhatsApp Butonu',
                button_name: 'WhatsApp',
                button_location: 'Fixed Bottom Right'
              });
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(37, 211, 102, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.4)';
          }}
          aria-label="WhatsApp ile iletişime geçin"
        >
          <svg
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="white"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.981-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        </a>
      </div>
    </>
  );
};

export default LandingPage;

